/**
 * P-046 — the names behind a `board_relation` cell.
 *
 * `board_relation` stores nothing but item ids (`{linkedPulseIds}`), and monday's own read
 * fragment answers `value: null` / `text: null` with the names in **`display_value`** — *"the
 * linked items' names, `", "`-joined"* (`specs/M2-tier2-column-types.md` §5.17, `[09§A.7]`).
 * The grid never asked anyone for those names, so every Connect-boards cell on a real board
 * rendered `#1782545680`: a raw database id, on the phone and on the desktop alike. That is
 * not a phone bug and it is not a permission bug — the lookup simply did not exist.
 *
 * It lives here, in the column's own folder, because it is this column type's business
 * (CLAUDE.md rule 6) and because `ItemRow.values` carries only the stored value: there is no
 * per-board name table to read it out of.
 *
 * **Permissions.** The lookup is `items.byIds`, and an item on a board the viewer cannot read
 * is *absent* from that answer, never an error (`apps/api/src/services/items.ts`, M0-F8 §7).
 * So an unreadable link resolves to `null` here and renders as nothing — exactly the
 * `display_value` monday computes, which joins only the names it could resolve. A name is
 * never invented and never leaked, and a bare id is never shown.
 *
 * One batched GET per screenful: every cell that mounts hands its ids to the same queue, the
 * queue flushes on the next macrotask in chunks of 100 (the procedure's own `max`), and a name
 * is fetched once per page load.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { TRPC_ENDPOINT, CLIENT_HEADER, CLIENT_HEADER_VALUE } from '../../../../lib/trpc';

/** `undefined` — not looked up yet · `string` — the item's name · `null` — no readable item. */
const names = new Map<string, string | null>();
const pending = new Set<string>();
const inFlight = new Set<string>();
const listeners = new Set<() => void>();
let version = 0;
let scheduled = false;

/** The `items.byIds` input cap (`z.array(idString).min(1).max(100)`). */
const CHUNK = 100;

function publish(): void {
  version += 1;
  for (const listener of [...listeners]) listener();
}

async function fetchChunk(ids: string[]): Promise<void> {
  const url = `${TRPC_ENDPOINT}/items.byIds?input=${encodeURIComponent(JSON.stringify({ ids }))}`;
  try {
    const response = await fetch(url, {
      credentials: 'include',
      headers: { [CLIENT_HEADER]: CLIENT_HEADER_VALUE },
    });
    if (!response.ok) throw new Error(String(response.status));
    const body = (await response.json()) as { result?: { data?: unknown } };
    const rows = Array.isArray(body?.result?.data) ? (body.result.data as Array<Record<string, unknown>>) : [];
    for (const row of rows) {
      const id = row?.id;
      const name = row?.name;
      if (id !== undefined && id !== null && typeof name === 'string') names.set(String(id), name);
    }
  } catch {
    /*
     * A failed lookup is recorded as "no readable name" rather than left pending: a chip that
     * spins for the rest of the session is worse than the empty cell monday's own
     * `display_value: ""` produces, and the next mount of the board re-asks.
     */
  }
  // Whatever came back, every id in the chunk is now decided; the misses are the unreadable ones.
  for (const id of ids) if (!names.has(id)) names.set(id, null);
  for (const id of ids) inFlight.delete(id);
}

function flush(): void {
  scheduled = false;
  const ids = [...pending];
  pending.clear();
  if (ids.length === 0) return;
  for (const id of ids) inFlight.add(id);
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += CHUNK) chunks.push(ids.slice(index, index + CHUNK));
  void Promise.all(chunks.map(fetchChunk)).then(publish);
}

/** Queue every id this caller needs that nobody has asked for yet. */
export function requestLinkedItemNames(ids: readonly (string | number)[]): void {
  let added = false;
  for (const raw of ids) {
    const id = String(raw);
    if (names.has(id) || pending.has(id) || inFlight.has(id)) continue;
    pending.add(id);
    added = true;
  }
  if (!added || scheduled) return;
  scheduled = true;
  // A macrotask, not a microtask: one flush for the whole screenful React is mounting.
  setTimeout(flush, 0);
}

/** The cached name, `null` when there is no readable item, `undefined` while it is unknown. */
export function linkedItemName(id: string | number): string | null | undefined {
  return names.get(String(id));
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Re-renders the cell when the names it is waiting for arrive. The ids are requested from an
 * effect so a render stays pure; `version` is the only external state the component reads.
 */
export function useLinkedItemNames(ids: readonly (string | number)[]): (id: string | number) => string | null | undefined {
  const key = ids.join(',');
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
  useEffect(() => {
    if (key !== '') requestLinkedItemNames(key.split(','));
  }, [key]);
  return linkedItemName;
}

/** Tests only: forget everything that was looked up. */
export function resetLinkedItemNames(): void {
  names.clear();
  pending.clear();
  inFlight.clear();
  publish();
}
